import React, { useState } from 'react';
import { useField } from 'formik';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import WarehouseCardSearch, { WarehouseCardSearchProps, WarehouseSearchStockCard } from '../WarehouseCardSearch/WarehouseCardSearch';
import { NumericInputField, SelectField } from '../../../../components/forms/fields';
import { InputFieldProps } from '../../../../components/forms/fields/InputField';
import { SelectFieldProps } from '../../../../components/forms/fields/SelectField';
import { StockCardsSearchQuery, VenueType } from '../../../../generated/graphql';
import { STOCK_QUANTITY_PRECISION } from '../../../../services/price-service/constants';
import { UnitTypeOptions, setUnitTypes } from '../../../../layout/unitTypes';
import ProtectedComponent from '../../../../utils/routes/ProtectedComponent';
import { ProductFormValues } from './types';
import WasteField from './WasteField';

type PickedWarehouseCardSearchProps = 'name' | 'warehouseIds' | 'currentWarehouseCard' | 'label' | 'helperText';

interface SearchFieldProps extends Pick<WarehouseCardSearchProps, PickedWarehouseCardSearchProps> {
    stockCardFieldName: string;
}

interface RecipeLineFieldProps {
    searchFieldProps: SearchFieldProps;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    warehouseFieldProps: Omit<SelectFieldProps<any>, 'options'>;
    unitTypeFieldProps: Omit<SelectFieldProps<any>, 'options'>;
    /* eslint-enable @typescript-eslint/no-explicit-any */
    quantityFieldProps: InputFieldProps<ProductFormValues>;
    absoluteWasteFieldProps: InputFieldProps<ProductFormValues>;
    relativeWasteFieldProps: InputFieldProps<ProductFormValues>;
    avaialbleWarehouseIds: string[];
}

type StockCardWarehouses = StockCardsSearchQuery['stockCards'][0]['warehouses'];

const RecipeLineField = ({
    searchFieldProps,
    warehouseFieldProps,
    avaialbleWarehouseIds,
    unitTypeFieldProps,
    quantityFieldProps,
    absoluteWasteFieldProps,
    relativeWasteFieldProps,
}: RecipeLineFieldProps) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    const storageUnit = searchFieldProps.currentWarehouseCard?.storageUnit;
    const conversionUnit = searchFieldProps.currentWarehouseCard?.conversionUnit;

    const [
        warehouses,
        setWarehouses,
    ] = useState<StockCardWarehouses>(searchFieldProps.currentWarehouseCard?.warehouses || []);

    const defaultUnitTypeOptions: Array<UnitTypeOptions> = [];
    setUnitTypes(defaultUnitTypeOptions, storageUnit, conversionUnit);

    const [
        unitTypeOptions,
        setUnitTypeOptions,
    ] = useState<Array<UnitTypeOptions>>(defaultUnitTypeOptions);

    const [, inputMeta, inputHelpers] = useField(searchFieldProps.name);
    const [, , warehouseFieldHelpers] = useField(warehouseFieldProps.name);
    const [, , unitTypeFieldHelpers] = useField(unitTypeFieldProps?.name ?? '');
    const [, { value: quantity }] = useField(quantityFieldProps.name);
    const [{ onBlur: onSearchFieldBlur }, , { setValue: setStockCardValue }] = useField(searchFieldProps.stockCardFieldName);

    const { value: inputValue } = inputMeta;
    const { setValue: setInputValue } = inputHelpers;
    const { setValue: setWarehouseValue } = warehouseFieldHelpers;
    const { setValue: setUnitTypeValue } = unitTypeFieldHelpers;

    const filteredWarehouses = warehouses.filter(({ id }) => avaialbleWarehouseIds.includes(id));

    const onWarehouseCardClick = (stockCard: WarehouseSearchStockCard | null) => {
        const stockCardWarehouses = stockCard?.warehouses ?? [];
        const newStorageUnit = stockCard?.storageUnit;
        const newConversionUnit = stockCard?.conversionUnit;
        const newUnitTypeOptions: Array<UnitTypeOptions> = [];
        setUnitTypes(newUnitTypeOptions, newStorageUnit, newConversionUnit);

        setStockCardValue(stockCard);
        setInputValue(stockCard?.id || null);
        setWarehouses(stockCardWarehouses);
        setUnitTypeOptions(newUnitTypeOptions);

        if (!stockCard) {
            setWarehouseValue(null);
            setUnitTypeValue(null);
        }

        if (stockCardWarehouses.length > 0) {
            setWarehouseValue(stockCardWarehouses[0].id);
        }

        if (newUnitTypeOptions.length > 0) {
            setUnitTypeValue(newStorageUnit);
        }

    };

    const flexDirection = isDesktop ? 'row' : 'column';

    return (
        <Box display="flex" flexDirection='column' justifyContent="space-between" style={{ width: '100%' }}>
            <Box display="flex" flexDirection={flexDirection}>
                <WarehouseCardSearch
                    {...searchFieldProps}
                    onBlur={onSearchFieldBlur}
                    onItemClick={onWarehouseCardClick}
                    value={inputValue}
                />
                <Box mr={2} />
                <SelectField
                    {...warehouseFieldProps}
                    disabled={warehouses.length === 0}
                    options={filteredWarehouses}
                />
            </Box>
            <Box display="flex" justifyContent="space-between" flexDirection={flexDirection}>
                <Box flex="1" display="flex">
                    <NumericInputField
                        {...quantityFieldProps}
                        disabled={!inputValue || inputValue === ''}
                    />
                    <Box mr={2} />
                    <SelectField
                        {...unitTypeFieldProps}
                        disabled={!inputValue || inputValue === ''}
                        options={unitTypeOptions}
                    />
                </Box>
                <ProtectedComponent allowedVenueTypes={[VenueType.Restaurant]}>
                    <>
                        <Box mr={isDesktop ? 2 : 0} />
                        <WasteField
                            maxDecimals={STOCK_QUANTITY_PRECISION}
                            isDisabled={!inputValue || inputValue === ''}
                            absoluteWasteFieldProps={absoluteWasteFieldProps}
                            relativeWasteFieldProps={relativeWasteFieldProps}
                            quantity={quantity}
                        />
                    </>
                </ProtectedComponent>
            </Box>
        </Box>
    );
};

export default RecipeLineField;
